import React, { useCallback, useEffect, useRef } from "react";
import { OpenTimeSeries } from "@captor/openseries-ts";
import { numberFormatFun } from "../../../common/src/utils/FormatFunctions";
import { CategoryBarChart } from "../../../components/src/charts/CategoryBarChart";
import { barFreqForPeriod, barFreqToResampleFreq, formatBarPeriodLabel, periodBarReturns } from "./barFreqForPeriod";

const format = numberFormatFun("0.0%");

type CompareAnnualBarChartProps = {
    timeseries: OpenTimeSeries[];
    colors: string[];
    startDate?: string;
    endDate?: string;
    style?: React.CSSProperties;
    /** Fired once after the chart mounts, or immediately when there is nothing to plot. */
    onInitialized?: () => void;
};

export const CompareAnnualBarChart = ({
    timeseries,
    colors,
    startDate,
    endDate,
    style,
    onInitialized
}: CompareAnnualBarChartProps): React.ReactElement => {
    const notifiedRef = useRef(false);
    const notifyInitialized = useCallback(() => {
        if (notifiedRef.current || !onInitialized) return;
        notifiedRef.current = true;
        onInitialized();
    }, [onInitialized]);
    const valid = timeseries?.filter((ts) => ts && ts.length > 0) ?? [];
    useEffect(() => {
        if (valid.length === 0) notifyInitialized();
    }, [valid.length, notifyInitialized]);
    if (valid.length === 0) return <div />;

    const masterTs = valid[0];
    const periodStart = startDate || masterTs.firstIdx;
    const periodEnd = endDate || masterTs.lastIdx;
    const barFreq = barFreqForPeriod(periodStart, periodEnd);
    const resampleFreq = barFreqToResampleFreq(barFreq);

    const rows: { category: string; series: string; value: number }[] = [];
    const names: string[] = [];
    valid.forEach((ts, idx) => {
        const { dates, returns } = periodBarReturns(ts, resampleFreq);
        const name = ts.label || ts.name || `Series ${idx}`;
        names.push(name);
        dates.forEach((dateStr, index) => {
            rows.push({
                category: formatBarPeriodLabel(dateStr, barFreq),
                series: name,
                value: returns[index]
            });
        });
    });

    const plotStyle = { width: "100%", height: "100%", ...style };

    return (
        <div style={plotStyle}>
            <CategoryBarChart
                rows={rows}
                orientation="vertical"
                mode="group"
                colors={colors}
                showLegend
                valueFormat={(value) => format(value)}
                ariaLabel={`Returns for ${names.join(", ")}`}
                height={360}
                style={{ width: "100%", height: "100%" }}
                onReady={notifyInitialized}
            />
        </div>
    );
};
